import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CharlesErrorState,
  getCharlesErrorCopy,
} from "@/components/employee/CharlesErrorStates";
import { CharlesPrivateBadge } from "@/components/employee/CharlesPrivateBadge";
import { useCharles } from "@/contexts/CharlesContext";
import { cn } from "@/lib/utils";
import { CssOrb } from "@/components/charles/CssOrb";
import { LiveScorePanel } from "@/components/charles/LiveScorePanel";

export default function Charles() {
  const {
    session,
    conversation,
    configError,
    started,
    forceTextOnly,
    micDenied,
    voiceOptIn,
    start,
    stop,
    sendText,
  } = useCharles();

  const [text, setText] = useState("");

  if (configError) {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center p-6">
        <CharlesErrorState code="config_error" />
      </div>
    );
  }
  if (session.status === "loading") {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (session.status === "error") {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center p-6">
        <CharlesErrorState code={session.code} />
      </div>
    );
  }

  const showVoiceUI = voiceOptIn && !forceTextOnly;
  const micNotice = micDenied ? getCharlesErrorCopy("mic_denied") : null;
  const isConnected = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;

  const statusLabel = !started
    ? showVoiceUI
      ? "Tap the sphere to start speaking"
      : "Tap the sphere to start chatting"
    : isConnected
      ? isSpeaking
        ? "Charles is speaking…"
        : "Listening"
      : "Connecting…";

  return (
    <div className="min-h-[100dvh] bg-white text-neutral-900 flex flex-col">
      {/* Minimal header */}
      <header className="px-6 pt-8 sm:pt-12 text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
          Talk to Charles
        </h1>
        <p className="text-sm text-neutral-500">
          A private space to think out loud.
        </p>
        <div className="flex justify-center pt-1">
          <CharlesPrivateBadge />
        </div>
      </header>

      {/* Centerpiece sphere */}
      <main className="flex-1 flex flex-col items-center gap-6 px-6">
        <div className="w-full flex items-center justify-center min-h-[340px] sm:min-h-[380px] py-8 sm:py-10">
          <button
            onClick={() => {
              if (!started) start();
            }}
            aria-label={started ? "Charles is active" : "Start conversation with Charles"}
            className={cn(
              "relative rounded-full transition-transform duration-300",
              !started && "hover:scale-[1.03] active:scale-[0.98] cursor-pointer",
              started && "cursor-default",
            )}
          >
            <CssOrb size={220} />
          </button>
        </div>

        <p className="text-sm text-neutral-500 text-center min-h-[1.25rem]">
          {statusLabel}
        </p>

        {started && <LiveScorePanel />}

        {micNotice && (
          <p className="max-w-sm text-xs text-neutral-500 text-center border border-neutral-200 rounded-md px-3 py-2">
            <span className="font-medium text-neutral-900">{micNotice.title}.</span>{" "}
            {micNotice.body}
          </p>
        )}

        {started && !showVoiceUI && (
          <div className="w-full max-w-md flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Say what's on your mind…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && text.trim()) {
                  sendText(text);
                  setText("");
                }
              }}
            />
            <Button
              onClick={() => {
                sendText(text);
                setText("");
              }}
            >
              Send
            </Button>
          </div>
        )}
      </main>

      {/* Footer action */}
      <footer className="px-6 pb-4 sm:pb-6 flex justify-center">
        {started ? (
          <Button
            variant="ghost"
            onClick={stop}
            className="text-neutral-500 hover:text-neutral-900"
          >
            End conversation
          </Button>
        ) : (
          <div className="h-10" />
        )}
      </footer>
    </div>
  );
}
